import React, { useState } from 'react';
import { Mail, Plus, RefreshCw } from 'lucide-react';
import Button from '../Button';
import { CODES, shipmentsApi } from '../../utils/shipmentsApi';
import { formatDateTime, relativeTime } from '../../utils/shipmentsFormat';
import { SelectField } from './FormFields';
import { Banner } from './Ui';

const MAX_MAILBOXES = 20;

const Time = ({ at, empty = 'nikdy' }) => (at ? <time dateTime={at} title={formatDateTime(at)}>{relativeTime(at)}</time> : <span className="shp-muted">{empty}</span>);

// History loading: "hotovo" or a progress bar over the query checkpoints
function Backfill({ mb }) {
  if (mb.backfillDoneAt) return <>hotovo, {formatDateTime(mb.backfillDoneAt)}</>;
  const { queryIndex, queryCount } = mb.backfillState || {};
  if (Number.isFinite(queryIndex) && Number.isFinite(queryCount) && queryCount > 0) {
    return (
      <>
        <progress className="shp-progress" max={queryCount} value={Math.min(queryIndex, queryCount)} aria-label="Načtení historie pošty" />
        <span className="shp-small shp-muted">část {Math.min(queryIndex + 1, queryCount)} z {queryCount}</span>
      </>
    );
  }
  return <span className="shp-muted">čeká na spuštění</span>;
}

// Mailboxes: switch, last run / last success, error, failures, history progress, "Synchronizovat teď", add a mailbox.
const MailboxesCard = ({ mailboxes, allowed = [], onChanged, notify }) => {
  const [choice, setChoice] = useState('');
  const [addError, setAddError] = useState('');
  // only addresses the server configuration allows and that are not in the table yet
  const taken = new Set(mailboxes.map((m) => m.mailbox.toLowerCase()));
  const available = allowed.filter((a) => typeof a === 'string' && !taken.has(a.toLowerCase()));
  const [adding, setAdding] = useState(false);
  const [busyKey, setBusyKey] = useState('');
  const [error, setError] = useState('');

  const act = async (key, task, success) => {
    setBusyKey(key);
    setError('');
    try {
      await task();
      if (success) notify.success(success);
      await onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyKey('');
    }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!choice) { setAddError('Vyberte schránku ze seznamu.'); return; }
    if (mailboxes.length >= MAX_MAILBOXES) { setAddError(`Lze přidat nejvýš ${MAX_MAILBOXES} schránek.`); return; }
    setAddError('');
    setAdding(true);
    try {
      await shipmentsApi.addMailbox(choice);
      notify.success('Schránka byla přidána. První načtení historie začne při nejbližším běhu.');
      setChoice('');
      await onChanged();
    } catch (err) {
      // 403 MAILBOX_NOT_ALLOWED: the list of allowed mailboxes changed on the server, refresh it
      setAddError(err.code === CODES.MAILBOX_NOT_ALLOWED
        ? 'Tuto schránku nelze přidat, není povolená v konfiguraci serveru. Nové schránky povoluje administrátor.'
        : err.message);
      if (err.code === CODES.MAILBOX_NOT_ALLOWED) { setChoice(''); await onChanged(); }
    } finally {
      setAdding(false);
    }
  };

  return (
    <section className="shp-card" aria-labelledby="shp-mailboxes-title">
      <h2 id="shp-mailboxes-title" className="shp-h2"><Mail size={18} color="#E53935" aria-hidden="true" /> Schránky ({mailboxes.length}/{MAX_MAILBOXES})</h2>
      <p className="shp-muted shp-small" style={{ margin: '0 0 12px' }}>
        ERP z těchto schránek jen čte zásilkové e-maily. Vypnutá schránka se přestane synchronizovat, ale už načtená data zůstanou.
      </p>
      {error && <Banner tone="bad">{error}</Banner>}
      {mailboxes.length === 0 && <p className="shp-muted">Zatím není přidaná žádná schránka.</p>}
      {mailboxes.map((mb) => (
        <article key={mb.mailbox} className="shp-mailbox" aria-label={`Schránka ${mb.mailbox}`}>
          <div className="shp-mailbox__head">
            <strong className="shp-wrap" dir="auto">{mb.mailbox}</strong>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span className="shp-small">{mb.enabled ? 'Zapnuto' : 'Vypnuto'}</span>
              <span className="shp-switchbox">
                <button
                  type="button" role="switch" aria-checked={mb.enabled} className="shp-switch" disabled={busyKey === `${mb.mailbox}:t`}
                  aria-label={`Synchronizace schránky ${mb.mailbox}`}
                  onClick={() => act(`${mb.mailbox}:t`, () => shipmentsApi.setMailboxEnabled(mb.mailbox, !mb.enabled), mb.enabled ? 'Schránka byla vypnuta.' : 'Schránka byla zapnuta.')}
                />
              </span>
            </span>
          </div>
          {mb.lastError && <Banner tone="bad">{mb.lastError}{mb.consecutiveFailures > 0 ? ` (neúspěšných pokusů po sobě: ${mb.consecutiveFailures})` : ''}</Banner>}
          <dl className="shp-kv">
            <div><dt>Poslední běh</dt><dd><Time at={mb.lastRunAt} /></dd></div>
            <div><dt>Poslední úspěšný běh</dt><dd><Time at={mb.lastOkAt} /></dd></div>
            <div><dt>Neúspěchů po sobě</dt><dd className="shp-num">{mb.consecutiveFailures ?? 0}</dd></div>
            <div><dt>Načtení historie</dt><dd style={{ display: 'grid', gap: 4 }}><Backfill mb={mb} /></dd></div>
          </dl>
          {mb.needsResync && <p className="shp-small" style={{ margin: 0 }}><span className="shp-tag shp-tag--warn">Čeká na opětovné načtení pošty</span></p>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Button
              variant="secondary" icon={RefreshCw} loading={busyKey === `${mb.mailbox}:s`} disabled={!mb.enabled}
              onClick={() => act(`${mb.mailbox}:s`, () => shipmentsApi.syncMailbox(mb.mailbox), 'Synchronizace byla vyžádána, začne do několika sekund.')}
            >Synchronizovat teď</Button>
            {mb.syncRequestedAt && <span className="shp-small shp-muted">Vyžádáno <Time at={mb.syncRequestedAt} /></span>}
          </div>
        </article>
      ))}

      {available.length > 0 && (
        <form onSubmit={add} noValidate style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap', marginTop: 16 }}>
          <div style={{ flex: '1 1 260px' }}>
            <SelectField
              label="Přidat schránku" value={choice} error={addError} placeholder="Vyberte povolenou schránku…"
              options={available.map((a) => ({ value: a, label: a }))}
              onChange={(e) => { setChoice(e.target.value); if (addError) setAddError(''); }}
            />
          </div>
          <div style={{ paddingTop: 25 }}><Button type="submit" icon={Plus} loading={adding} disabled={!choice}>Přidat</Button></div>
        </form>
      )}
      {addError && available.length === 0 && <p role="alert" className="shp-error" style={{ marginTop: 12 }}>{addError}</p>}
      <p className="shp-muted shp-small" style={{ margin: '14px 0 0' }}>
        Nové schránky, které lze sem přidat, povoluje administrátor v konfiguraci serveru.
      </p>
    </section>
  );
};

export default MailboxesCard;
